/**
 * IntakePage.jsx — Multi-step Clinical Intake Form
 *
 * Steps:
 *   1. Demographics (name, DOB, contact, address)
 *   2. Emergency Contacts
 *   3. Insurance Information
 *   4. Clinical History (presenting problems, risk screening)
 *   5. Program & Provider Assignment
 *   6. Consents
 *   7. Review & Submit
 *
 * Matches existing Field Notes dark theme.
 * Powered by Cold Harbor Digital
 */

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  completeIntake,
  getTeamMembers,
  CONSENT_TYPES,
  INSURANCE_TYPES,
  PROGRAMS,
  REFERRAL_SOURCES,
} from "../services/intakeService";
import {
  ArrowLeft, ArrowRight, Check, User, Phone, Shield,
  Heart, Users, FileText, ClipboardList, AlertTriangle,
  Plus, X, Home, Calendar, DollarSign, Activity,
} from "lucide-react";
import BottomNav from "../components/BottomNav";

const STEPS = [
  { key: "demographics", label: "Demographics", icon: User },
  { key: "emergency", label: "Emergency", icon: Phone },
  { key: "insurance", label: "Insurance", icon: DollarSign },
  { key: "clinical", label: "Clinical", icon: Heart },
  { key: "assignment", label: "Assignment", icon: Users },
  { key: "consents", label: "Consents", icon: Shield },
  { key: "review", label: "Review", icon: ClipboardList },
];

// Shared field styles
const S = {
  label: { display: "block", fontSize: 11, fontWeight: 700, color: "#a0aec0", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 },
  input: {
    width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, fontWeight: 500,
    background: "rgba(15,23,42,0.6)", color: "#e2e8f0",
    border: "1px solid rgba(99,179,237,0.1)", outline: "none",
    boxSizing: "border-box",
  },
  select: {
    width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, fontWeight: 500,
    background: "rgba(15,23,42,0.6)", color: "#e2e8f0",
    border: "1px solid rgba(99,179,237,0.1)", outline: "none",
    appearance: "auto", boxSizing: "border-box",
  },
  textarea: {
    width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, fontWeight: 500,
    background: "rgba(15,23,42,0.6)", color: "#e2e8f0",
    border: "1px solid rgba(99,179,237,0.1)", outline: "none",
    minHeight: 80, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box",
  },
  card: {
    background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,179,237,0.08)",
    borderRadius: 16, padding: 16, marginBottom: 12,
  },
  sectionTitle: { fontSize: 13, fontWeight: 800, color: "#63b3ed", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 },
  row: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  fieldGroup: { marginBottom: 12 },
};

export default function IntakePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  const referralData = location.state?.referral || null;

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);

  // ─── Form State ───────────────────────────────────────────────
  const [form, setForm] = useState({
    // Step 1: Demographics
    firstName: referralData?.firstName || "",
    lastName: referralData?.lastName || "",
    preferredName: "",
    dob: referralData?.dob || "",
    ssn: "",
    gender: "",
    pronouns: "",
    race: "",
    ethnicity: "",
    preferredLanguage: "English",
    maritalStatus: "",
    phone: referralData?.phone || "",
    email: referralData?.email || "",
    address: referralData?.address || "",
    city: "",
    state: "GA",
    zip: "",
    county: "",

    // Step 2: Emergency Contacts
    emergencyContacts: [
      { name: "", relation: "", phone: "", isPrimary: true },
    ],

    // Step 3: Insurance
    insuranceType: referralData?.insuranceType || "",
    insurancePrimaryPayer: "",
    insurancePrimaryPayerId: "",
    insurancePrimaryMemberId: referralData?.insuranceId || "",
    insurancePrimaryGroup: "",
    insurancePrimarySubscriber: "",
    insurancePrimaryRelation: "self",
    insurancePrimaryEffective: "",
    insurancePrimaryExpiration: "",
    hasSecondaryInsurance: false,
    insuranceSecondaryPayer: "",
    insuranceSecondaryMemberId: "",
    insuranceSecondaryGroup: "",

    // Step 4: Clinical
    presentingProblems: "",
    historyMentalHealth: "",
    historySubstanceUse: "",
    historyMedical: "",
    historyTrauma: "",
    suicidalHistory: "none",
    riskLevel: "low",
    diagnoses: [],
    medications: [],
    allergies: [],

    // Step 5: Assignment
    program: referralData?.requestedProgram || "ICM",
    referralSource: referralData?.sourceType || "",
    referralId: referralData?.id || "",
    assignedAgent: "",
    assignedAgentName: "",
    assignedClinician: "",
    assignedClinicianName: "",
    assignedPrescriber: "",
    assignedPrescriberName: "",

    // Step 6: Consents
    consents: CONSENT_TYPES.map(c => ({ ...c, signed: false })),
    consentSignedByRelation: "self",
  });

  // Load team members for assignment step
  useEffect(() => {
    const org = profile?.organization || localStorage.getItem("fieldnotes_user_org") || "default-org";
    getTeamMembers(org).then(setTeamMembers);
  }, [profile]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const currentStep = STEPS[step];
  const isFirstStep = step === 0;
  const isLastStep = step === STEPS.length - 1;

  // ─── Validation ───────────────────────────────────────────────
  const validateStep = () => {
    switch (currentStep.key) {
      case "demographics":
        if (!form.firstName.trim() || !form.lastName.trim()) return "First and last name are required.";
        if (!form.dob) return "Date of birth is required.";
        return null;
      case "consents": {
        const requiredUnsigned = form.consents.filter(c => c.required && !c.signed);
        if (requiredUnsigned.length > 0) return `Required consents must be signed: ${requiredUnsigned.map(c => c.label).join(", ")}`;
        return null;
      }
      default:
        return null;
    }
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError("");
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setError("");
    setStep(s => Math.max(s - 1, 0));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError("");
    const result = await completeIntake(form);
    if (result.success) {
      navigate(`/individual/${result.id}`, { state: { justCreated: true } });
    } else {
      setError(result.error || "Failed to complete intake.");
      setSaving(false);
    }
  };

  // ─── Emergency Contact Helpers ────────────────────────────────
  const addEmergencyContact = () => {
    update("emergencyContacts", [
      ...form.emergencyContacts,
      { name: "", relation: "", phone: "", isPrimary: false },
    ]);
  };
  const updateEmergencyContact = (index, field, value) => {
    const updated = [...form.emergencyContacts];
    updated[index] = { ...updated[index], [field]: value };
    update("emergencyContacts", updated);
  };
  const removeEmergencyContact = (index) => {
    update("emergencyContacts", form.emergencyContacts.filter((_, i) => i !== index));
  };

  // ─── Diagnosis / Medication / Allergy Helpers ─────────────────
  const addDiagnosis = () => update("diagnoses", [...form.diagnoses, { code: "", description: "", type: "primary", status: "active" }]);
  const updateDiagnosis = (i, field, val) => {
    const arr = [...form.diagnoses]; arr[i] = { ...arr[i], [field]: val }; update("diagnoses", arr);
  };
  const removeDiagnosis = (i) => update("diagnoses", form.diagnoses.filter((_, idx) => idx !== i));

  const addMedication = () => update("medications", [...form.medications, { name: "", dosage: "", frequency: "", prescriber: "", status: "active" }]);
  const updateMedication = (i, field, val) => {
    const arr = [...form.medications]; arr[i] = { ...arr[i], [field]: val }; update("medications", arr);
  };
  const removeMedication = (i) => update("medications", form.medications.filter((_, idx) => idx !== i));

  const addAllergy = () => update("allergies", [...form.allergies, { allergen: "", reaction: "", severity: "" }]);
  const updateAllergy = (i, field, val) => {
    const arr = [...form.allergies]; arr[i] = { ...arr[i], [field]: val }; update("allergies", arr);
  };
  const removeAllergy = (i) => update("allergies", form.allergies.filter((_, idx) => idx !== i));

  // ─── Render ───────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#0a0f1a", color: "#e2e8f0", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>

      {/* Header */}
      <header style={{
        padding: "16px 20px", background: "linear-gradient(135deg, #0f1729, #162033)",
        borderBottom: "1px solid rgba(246,173,85,0.15)", position: "sticky", top: 0, zIndex: 50,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "#a0aec0" }}>
              <ArrowLeft size={20} />
            </button>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#f6ad55" }}>New Intake</div>
              <div style={{ fontSize: 11, color: "#718096" }}>
                Step {step + 1} of {STEPS.length} — {currentStep.label}
              </div>
            </div>
          </div>
          {form.firstName && form.lastName && (
            <div style={{ fontSize: 12, fontWeight: 700, color: "#a0aec0" }}>
              {form.firstName} {form.lastName}
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div style={{ display: "flex", gap: 3, marginTop: 12 }}>
          {STEPS.map((s, i) => (
            <div key={s.key} style={{
              flex: 1, height: 3, borderRadius: 2,
              background: i <= step ? "#f6ad55" : "rgba(246,173,85,0.15)",
              transition: "background 0.3s",
            }} />
          ))}
        </div>
      </header>

      {/* Step Content */}
      <div style={{ padding: "16px 20px", paddingBottom: 140 }}>

        {/* Error Banner */}
        {error && (
          <div style={{
            ...S.card,
            borderColor: "rgba(252,129,129,0.2)", background: "rgba(252,129,129,0.06)",
            display: "flex", alignItems: "center", gap: 8, marginBottom: 16,
          }}>
            <AlertTriangle size={16} style={{ color: "#fc8181", flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: "#fc8181", fontWeight: 600 }}>{error}</span>
          </div>
        )}

        {/* ===== STEP 1: DEMOGRAPHICS ===== */}
        {currentStep.key === "demographics" && (
          <div>
            <div style={S.sectionTitle}><User size={16} /> Demographics</div>

            <div style={S.row}>
              <div style={S.fieldGroup}>
                <label style={S.label}>First Name *</label>
                <input style={S.input} value={form.firstName} onChange={e => update("firstName", e.target.value)} placeholder="First name" />
              </div>
              <div style={S.fieldGroup}>
                <label style={S.label}>Last Name *</label>
                <input style={S.input} value={form.lastName} onChange={e => update("lastName", e.target.value)} placeholder="Last name" />
              </div>
            </div>

            <div style={S.row}>
              <div style={S.fieldGroup}>
                <label style={S.label}>Preferred Name</label>
                <input style={S.input} value={form.preferredName} onChange={e => update("preferredName", e.target.value)} placeholder="Nickname / preferred" />
              </div>
              <div style={S.fieldGroup}>
                <label style={S.label}>Date of Birth *</label>
                <input type="date" style={S.input} value={form.dob} onChange={e => update("dob", e.target.value)} />
              </div>
            </div>

            <div style={S.row}>
              <div style={S.fieldGroup}>
                <label style={S.label}>Gender</label>
                <select style={S.select} value={form.gender} onChange={e => update("gender", e.target.value)}>
                  <option value="">Select...</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="nonbinary">Non-binary</option>
                  <option value="transgender_male">Transgender Male</option>
                  <option value="transgender_female">Transgender Female</option>
                  <option value="other">Other</option>
                  <option value="decline">Decline to answer</option>
                </select>
              </div>
              <div style={S.fieldGroup}>
                <label style={S.label}>Pronouns</label>
                <select style={S.select} value={form.pronouns} onChange={e => update("pronouns", e.target.value)}>
                  <option value="">Select...</option>
                  <option value="he/him">He/Him</option>
                  <option value="she/her">She/Her</option>
                  <option value="they/them">They/Them</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div style={S.row}>
              <div style={S.fieldGroup}>
                <label style={S.label}>Race</label>
                <select style={S.select} value={form.race} onChange={e => update("race", e.target.value)}>
                  <option value="">Select...</option>
                  <option value="white">White</option>
                  <option value="black">Black / African American</option>
                  <option value="asian">Asian</option>
                  <option value="native_american">American Indian / Alaska Native</option>
                  <option value="pacific_islander">Native Hawaiian / Pacific Islander</option>
                  <option value="multiracial">Multiracial</option>
                  <option value="other">Other</option>
                  <option value="decline">Decline to answer</option>
                </select>
              </div>
              <div style={S.fieldGroup}>
                <label style={S.label}>Ethnicity</label>
                <select style={S.select} value={form.ethnicity} onChange={e => update("ethnicity", e.target.value)}>
                  <option value="">Select...</option>
                  <option value="hispanic">Hispanic / Latino</option>
                  <option value="not_hispanic">Not Hispanic / Latino</option>
                  <option value="decline">Decline to answer</option>
                </select>
              </div>
            </div>

            <div style={S.row}>
              <div style={S.fieldGroup}>
                <label style={S.label}>Preferred Language</label>
                <input style={S.input} value={form.preferredLanguage} onChange={e => update("preferredLanguage", e.target.value)} />
              </div>
              <div style={S.fieldGroup}>
                <label style={S.label}>Marital Status</label>
                <select style={S.select} value={form.maritalStatus} onChange={e => update("maritalStatus", e.target.value)}>
                  <option value="">Select...</option>
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="divorced">Divorced</option>
                  <option value="separated">Separated</option>
                  <option value="widowed">Widowed</option>
                  <option value="partnered">Domestic Partner</option>
                </select>
              </div>
            </div>

            <div style={{ ...S.sectionTitle, marginTop: 20 }}><Phone size={16} /> Contact Information</div>

            <div style={S.row}>
              <div style={S.fieldGroup}>
                <label style={S.label}>Phone</label>
                <input type="tel" style={S.input} value={form.phone} onChange={e => update("phone", e.target.value)} placeholder="(xxx) xxx-xxxx" />
              </div>
              <div style={S.fieldGroup}>
                <label style={S.label}>Email</label>
                <input type="email" style={S.input} value={form.email} onChange={e => update("email", e.target.value)} placeholder="email@example.com" />
              </div>
            </div>

            <div style={S.fieldGroup}>
              <label style={S.label}>Street Address</label>
              <input style={S.input} value={form.address} onChange={e => update("address", e.target.value)} placeholder="123 Main St" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }}>
              <div style={S.fieldGroup}>
                <label style={S.label}>City</label>
                <input style={S.input} value={form.city} onChange={e => update("city", e.target.value)} />
              </div>
              <div style={S.fieldGroup}>
                <label style={S.label}>State</label>
                <input style={S.input} value={form.state} onChange={e => update("state", e.target.value)} />
              </div>
              <div style={S.fieldGroup}>
                <label style={S.label}>ZIP</label>
                <input style={S.input} value={form.zip} onChange={e => update("zip", e.target.value)} />
              </div>
            </div>
            <div style={S.fieldGroup}>
              <label style={S.label}>County</label>
              <input style={S.input} value={form.county} onChange={e => update("county", e.target.value)} />
            </div>
          </div>
        )}

        {/* ===== STEP 2: EMERGENCY CONTACTS ===== */}
        {currentStep.key === "emergency" && (
          <div>
            <div style={S.sectionTitle}><Phone size={16} /> Emergency Contacts</div>

            {form.emergencyContacts.map((ec, i) => (
              <div key={i} style={{ ...S.card, borderColor: ec.isPrimary ? "rgba(246,173,85,0.15)" : undefined }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: ec.isPrimary ? "#f6ad55" : "#a0aec0" }}>
                    {ec.isPrimary ? "Primary Contact" : `Contact ${i + 1}`}
                  </span>
                  {!ec.isPrimary && (
                    <button onClick={() => removeEmergencyContact(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#fc8181" }}>
                      <X size={16} />
                    </button>
                  )}
                </div>
                <div style={S.row}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Name</label>
                    <input style={S.input} value={ec.name} onChange={e => updateEmergencyContact(i, "name", e.target.value)} placeholder="Full name" />
                  </div>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Relationship</label>
                    <input style={S.input} value={ec.relation} onChange={e => updateEmergencyContact(i, "relation", e.target.value)} placeholder="Mother, spouse, etc." />
                  </div>
                </div>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Phone</label>
                  <input type="tel" style={S.input} value={ec.phone} onChange={e => updateEmergencyContact(i, "phone", e.target.value)} placeholder="(xxx) xxx-xxxx" />
                </div>
              </div>
            ))}

            <button onClick={addEmergencyContact} style={{
              width: "100%", padding: "12px 0", borderRadius: 12, fontSize: 13, fontWeight: 700,
              background: "rgba(246,173,85,0.08)", color: "#f6ad55",
              border: "1px solid rgba(246,173,85,0.15)", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <Plus size={14} /> Add Emergency Contact
            </button>
          </div>
        )}

        {/* ===== STEP 3: INSURANCE ===== */}
        {currentStep.key === "insurance" && (
          <div>
            <div style={S.sectionTitle}><DollarSign size={16} /> Insurance Information</div>

            <div style={S.fieldGroup}>
              <label style={S.label}>Insurance Type</label>
              <select style={S.select} value={form.insuranceType} onChange={e => update("insuranceType", e.target.value)}>
                <option value="">Select...</option>
                {INSURANCE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            {form.insuranceType && form.insuranceType !== "self_pay" && form.insuranceType !== "uninsured" && (
              <div style={S.card}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#63b3ed", marginBottom: 10 }}>Primary Insurance</div>

                <div style={S.row}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Payer Name</label>
                    <input style={S.input} value={form.insurancePrimaryPayer} onChange={e => update("insurancePrimaryPayer", e.target.value)} placeholder="e.g. Amerigroup, Peach State" />
                  </div>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Payer ID</label>
                    <input style={S.input} value={form.insurancePrimaryPayerId} onChange={e => update("insurancePrimaryPayerId", e.target.value)} />
                  </div>
                </div>
                <div style={S.row}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Member ID</label>
                    <input style={S.input} value={form.insurancePrimaryMemberId} onChange={e => update("insurancePrimaryMemberId", e.target.value)} />
                  </div>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Group Number</label>
                    <input style={S.input} value={form.insurancePrimaryGroup} onChange={e => update("insurancePrimaryGroup", e.target.value)} />
                  </div>
                </div>
                <div style={S.row}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Subscriber Name</label>
                    <input style={S.input} value={form.insurancePrimarySubscriber} onChange={e => update("insurancePrimarySubscriber", e.target.value)} placeholder="If different from individual" />
                  </div>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Subscriber Relation</label>
                    <select style={S.select} value={form.insurancePrimaryRelation} onChange={e => update("insurancePrimaryRelation", e.target.value)}>
                      <option value="self">Self</option>
                      <option value="spouse">Spouse</option>
                      <option value="child">Child</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div style={S.row}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Effective Date</label>
                    <input type="date" style={S.input} value={form.insurancePrimaryEffective} onChange={e => update("insurancePrimaryEffective", e.target.value)} />
                  </div>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Expiration Date</label>
                    <input type="date" style={S.input} value={form.insurancePrimaryExpiration} onChange={e => update("insurancePrimaryExpiration", e.target.value)} />
                  </div>
                </div>
              </div>
            )}

            {form.insuranceType && form.insuranceType !== "self_pay" && form.insuranceType !== "uninsured" && (
              <div style={S.fieldGroup}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.hasSecondaryInsurance} onChange={e => update("hasSecondaryInsurance", e.target.checked)} />
                  <span style={{ fontSize: 13, color: "#a0aec0" }}>Has secondary insurance</span>
                </label>
              </div>
            )}

            {form.hasSecondaryInsurance && (
              <div style={S.card}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#63b3ed", marginBottom: 10 }}>Secondary Insurance</div>
                <div style={S.row}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Payer Name</label>
                    <input style={S.input} value={form.insuranceSecondaryPayer} onChange={e => update("insuranceSecondaryPayer", e.target.value)} />
                  </div>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Member ID</label>
                    <input style={S.input} value={form.insuranceSecondaryMemberId} onChange={e => update("insuranceSecondaryMemberId", e.target.value)} />
                  </div>
                </div>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Group Number</label>
                  <input style={S.input} value={form.insuranceSecondaryGroup} onChange={e => update("insuranceSecondaryGroup", e.target.value)} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== STEP 4: CLINICAL HISTORY ===== */}
        {currentStep.key === "clinical" && (
          <div>
            <div style={S.sectionTitle}><Heart size={16} /> Clinical History</div>

            <div style={S.fieldGroup}>
              <label style={S.label}>Presenting Problems / Reason for Services</label>
              <textarea style={S.textarea} value={form.presentingProblems} onChange={e => update("presentingProblems", e.target.value)}
                placeholder="Describe the individual's primary concerns, symptoms, and reasons for seeking services..." />
            </div>

            <div style={S.fieldGroup}>
              <label style={S.label}>Mental Health History</label>
              <textarea style={S.textarea} value={form.historyMentalHealth} onChange={e => update("historyMentalHealth", e.target.value)}
                placeholder="Previous diagnoses, hospitalizations, treatment history..." />
            </div>

            <div style={S.fieldGroup}>
              <label style={S.label}>Substance Use History</label>
              <textarea style={S.textarea} value={form.historySubstanceUse} onChange={e => update("historySubstanceUse", e.target.value)}
                placeholder="Current/past substance use, treatment history..." />
            </div>

            <div style={S.fieldGroup}>
              <label style={S.label}>Medical History</label>
              <textarea style={S.textarea} value={form.historyMedical} onChange={e => update("historyMedical", e.target.value)}
                placeholder="Chronic conditions, surgeries, current medical providers..." />
            </div>

            <div style={S.fieldGroup}>
              <label style={S.label}>Trauma History</label>
              <textarea style={S.textarea} value={form.historyTrauma} onChange={e => update("historyTrauma", e.target.value)}
                placeholder="Relevant trauma history (brief — detailed assessment to follow)..." />
            </div>

            {/* Risk Screening */}
            <div style={{ ...S.card, borderColor: "rgba(252,129,129,0.12)", marginTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#fc8181", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <AlertTriangle size={14} /> Risk Screening
              </div>

              <div style={S.fieldGroup}>
                <label style={S.label}>Suicidal History</label>
                <select style={S.select} value={form.suicidalHistory} onChange={e => update("suicidalHistory", e.target.value)}>
                  <option value="none">No history</option>
                  <option value="past_ideation">Past ideation (no attempt)</option>
                  <option value="past_attempt">Past attempt(s)</option>
                  <option value="current_ideation">Current ideation — SAFETY PLAN REQUIRED</option>
                </select>
              </div>

              <div style={S.fieldGroup}>
                <label style={S.label}>Current Risk Level</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {["low", "moderate", "high"].map(level => (
                    <button key={level} onClick={() => update("riskLevel", level)} style={{
                      flex: 1, padding: "10px 0", borderRadius: 10, fontSize: 12, fontWeight: 700,
                      textTransform: "capitalize", cursor: "pointer",
                      background: form.riskLevel === level
                        ? (level === "high" ? "rgba(252,129,129,0.15)" : level === "moderate" ? "rgba(236,201,75,0.15)" : "rgba(104,211,145,0.15)")
                        : "rgba(15,23,42,0.6)",
                      color: form.riskLevel === level
                        ? (level === "high" ? "#fc8181" : level === "moderate" ? "#ecc94b" : "#68d391")
                        : "#718096",
                      border: `1px solid ${form.riskLevel === level
                        ? (level === "high" ? "rgba(252,129,129,0.3)" : level === "moderate" ? "rgba(236,201,75,0.3)" : "rgba(104,211,145,0.3)")
                        : "rgba(99,179,237,0.08)"}`,
                    }}>
                      {level}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Known Diagnoses */}
            <div style={{ marginTop: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#b794f4" }}>Known Diagnoses</span>
                <button onClick={addDiagnosis} style={{ background: "none", border: "none", cursor: "pointer", color: "#b794f4", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                  <Plus size={12} /> Add
                </button>
              </div>
              {form.diagnoses.map((dx, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                  <input style={{ ...S.input, flex: "0 0 100px" }} value={dx.code} onChange={e => updateDiagnosis(i, "code", e.target.value)} placeholder="ICD-10" />
                  <input style={{ ...S.input, flex: 1 }} value={dx.description} onChange={e => updateDiagnosis(i, "description", e.target.value)} placeholder="Description" />
                  <button onClick={() => removeDiagnosis(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#fc8181" }}><X size={14} /></button>
                </div>
              ))}
            </div>

            {/* Current Medications */}
            <div style={{ marginTop: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#4fd1c5" }}>Current Medications</span>
                <button onClick={addMedication} style={{ background: "none", border: "none", cursor: "pointer", color: "#4fd1c5", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                  <Plus size={12} /> Add
                </button>
              </div>
              {form.medications.map((med, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                  <input style={{ ...S.input, flex: 1 }} value={med.name} onChange={e => updateMedication(i, "name", e.target.value)} placeholder="Medication" />
                  <input style={{ ...S.input, flex: "0 0 80px" }} value={med.dosage} onChange={e => updateMedication(i, "dosage", e.target.value)} placeholder="Dosage" />
                  <input style={{ ...S.input, flex: "0 0 80px" }} value={med.frequency} onChange={e => updateMedication(i, "frequency", e.target.value)} placeholder="Frequency" />
                  <button onClick={() => removeMedication(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#fc8181" }}><X size={14} /></button>
                </div>
              ))}
            </div>

            {/* Allergies */}
            <div style={{ marginTop: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#fc8181" }}>Allergies</span>
                <button onClick={addAllergy} style={{ background: "none", border: "none", cursor: "pointer", color: "#fc8181", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                  <Plus size={12} /> Add
                </button>
              </div>
              {form.allergies.map((a, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                  <input style={{ ...S.input, flex: 1 }} value={a.allergen} onChange={e => updateAllergy(i, "allergen", e.target.value)} placeholder="Allergen" />
                  <input style={{ ...S.input, flex: 1 }} value={a.reaction} onChange={e => updateAllergy(i, "reaction", e.target.value)} placeholder="Reaction" />
                  <select style={{ ...S.select, flex: "0 0 90px" }} value={a.severity} onChange={e => updateAllergy(i, "severity", e.target.value)}>
                    <option value="">Severity</option>
                    <option value="mild">Mild</option>
                    <option value="moderate">Moderate</option>
                    <option value="severe">Severe</option>
                  </select>
                  <button onClick={() => removeAllergy(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#fc8181" }}><X size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== STEP 5: PROGRAM & ASSIGNMENT ===== */}
        {currentStep.key === "assignment" && (
          <div>
            <div style={S.sectionTitle}><Users size={16} /> Program & Provider Assignment</div>

            <div style={S.fieldGroup}>
              <label style={S.label}>Program</label>
              <select style={S.select} value={form.program} onChange={e => update("program", e.target.value)}>
                {PROGRAMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>

            <div style={S.fieldGroup}>
              <label style={S.label}>Referral Source</label>
              <select style={S.select} value={form.referralSource} onChange={e => update("referralSource", e.target.value)}>
                <option value="">Select...</option>
                {REFERRAL_SOURCES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>

            <div style={{ ...S.sectionTitle, marginTop: 20 }}><Activity size={16} /> Assign Providers</div>

            {/* Agent */}
            <div style={S.fieldGroup}>
              <label style={S.label}>Assigned Case Manager / Agent</label>
              <select style={S.select} value={form.assignedAgent} onChange={e => {
                const member = teamMembers.find(m => m.id === e.target.value);
                update("assignedAgent", e.target.value);
                update("assignedAgentName", member?.displayName || "");
              }}>
                <option value="">Select agent...</option>
                {teamMembers.filter(m => ["agent", "supervisor", "admin"].includes(m.role)).map(m => (
                  <option key={m.id} value={m.id}>{m.displayName} ({m.role})</option>
                ))}
              </select>
            </div>

            {/* Clinician */}
            <div style={S.fieldGroup}>
              <label style={S.label}>Assigned Clinician (optional)</label>
              <select style={S.select} value={form.assignedClinician} onChange={e => {
                const member = teamMembers.find(m => m.id === e.target.value);
                update("assignedClinician", e.target.value);
                update("assignedClinicianName", member?.displayName || "");
              }}>
                <option value="">None assigned</option>
                {teamMembers.filter(m => ["clinician", "supervisor", "admin"].includes(m.role)).map(m => (
                  <option key={m.id} value={m.id}>{m.displayName} ({m.role})</option>
                ))}
              </select>
            </div>

            {/* Prescriber */}
            <div style={S.fieldGroup}>
              <label style={S.label}>Assigned Prescriber (optional)</label>
              <select style={S.select} value={form.assignedPrescriber} onChange={e => {
                const member = teamMembers.find(m => m.id === e.target.value);
                update("assignedPrescriber", e.target.value);
                update("assignedPrescriberName", member?.displayName || "");
              }}>
                <option value="">None assigned</option>
                {teamMembers.filter(m => ["prescriber", "supervisor", "admin"].includes(m.role)).map(m => (
                  <option key={m.id} value={m.id}>{m.displayName} ({m.role})</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* ===== STEP 6: CONSENTS ===== */}
        {currentStep.key === "consents" && (
          <div>
            <div style={S.sectionTitle}><Shield size={16} /> Consent Forms</div>

            <div style={{ ...S.card, marginBottom: 16, borderColor: "rgba(99,179,237,0.12)" }}>
              <div style={{ fontSize: 12, color: "#a0aec0", lineHeight: 1.6 }}>
                The following consents are acknowledged verbally or by signature. Check each box to confirm the individual (or their legal representative) has been informed and consents.
              </div>
              <div style={{ marginTop: 8 }}>
                <label style={S.label}>Who is signing?</label>
                <select style={S.select} value={form.consentSignedByRelation} onChange={e => update("consentSignedByRelation", e.target.value)}>
                  <option value="self">Individual (Self)</option>
                  <option value="guardian">Legal Guardian</option>
                  <option value="parent">Parent</option>
                  <option value="poa">Power of Attorney</option>
                </select>
              </div>
            </div>

            {form.consents.map((consent, i) => (
              <div key={consent.id} style={{
                ...S.card,
                borderColor: consent.signed ? "rgba(104,211,145,0.15)" : consent.required ? "rgba(252,129,129,0.1)" : undefined,
              }}>
                <label style={{ display: "flex", gap: 12, cursor: "pointer", alignItems: "flex-start" }}>
                  <input type="checkbox" checked={consent.signed}
                    onChange={e => {
                      const updated = [...form.consents];
                      updated[i] = { ...updated[i], signed: e.target.checked };
                      update("consents", updated);
                    }}
                    style={{ marginTop: 3, flexShrink: 0 }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: consent.signed ? "#68d391" : "#e2e8f0", display: "flex", alignItems: "center", gap: 6 }}>
                      {consent.label}
                      {consent.required && <span style={{ fontSize: 9, fontWeight: 800, color: "#fc8181", textTransform: "uppercase" }}>Required</span>}
                    </div>
                    <div style={{ fontSize: 11, color: "#718096", lineHeight: 1.5, marginTop: 4 }}>
                      {consent.description}
                    </div>
                  </div>
                </label>
              </div>
            ))}
          </div>
        )}

        {/* ===== STEP 7: REVIEW & SUBMIT ===== */}
        {currentStep.key === "review" && (
          <div>
            <div style={S.sectionTitle}><ClipboardList size={16} /> Review & Submit</div>

            {/* Demographics Summary */}
            <div style={S.card}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Individual</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#e2e8f0" }}>{form.firstName} {form.lastName}</div>
              {form.preferredName && <div style={{ fontSize: 12, color: "#718096" }}>Preferred: {form.preferredName}</div>}
              <div style={{ fontSize: 12, color: "#a0aec0", marginTop: 4 }}>DOB: {form.dob} · {form.gender || "—"} · {form.preferredLanguage}</div>
              {form.phone && <div style={{ fontSize: 12, color: "#a0aec0" }}>{form.phone} · {form.email}</div>}
              {form.address && <div style={{ fontSize: 12, color: "#a0aec0" }}>{form.address}{form.city ? `, ${form.city}` : ""} {form.state} {form.zip}</div>}
            </div>

            {/* Emergency Contacts */}
            {form.emergencyContacts.filter(ec => ec.name).length > 0 && (
              <div style={S.card}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Emergency Contacts</div>
                {form.emergencyContacts.filter(ec => ec.name).map((ec, i) => (
                  <div key={i} style={{ fontSize: 12, color: "#a0aec0", marginBottom: 4 }}>
                    {ec.name} ({ec.relation}) — {ec.phone}
                  </div>
                ))}
              </div>
            )}

            {/* Insurance */}
            <div style={S.card}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Insurance</div>
              <div style={{ fontSize: 12, color: "#a0aec0" }}>
                {INSURANCE_TYPES.find(t => t.value === form.insuranceType)?.label || "Not specified"}
                {form.insurancePrimaryPayer && ` — ${form.insurancePrimaryPayer}`}
                {form.insurancePrimaryMemberId && ` (ID: ${form.insurancePrimaryMemberId})`}
              </div>
            </div>

            {/* Clinical */}
            <div style={S.card}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Clinical</div>
              <div style={{ fontSize: 12, color: "#a0aec0", marginBottom: 4 }}>
                Risk: <span style={{ color: form.riskLevel === "high" ? "#fc8181" : form.riskLevel === "moderate" ? "#ecc94b" : "#68d391", fontWeight: 700 }}>{form.riskLevel}</span>
                {form.suicidalHistory !== "none" && <span style={{ color: "#fc8181", marginLeft: 8 }}>⚠ {form.suicidalHistory.replace(/_/g, " ")}</span>}
              </div>
              {form.diagnoses.length > 0 && (
                <div style={{ fontSize: 12, color: "#a0aec0" }}>
                  Diagnoses: {form.diagnoses.map(d => `${d.code} ${d.description}`).join(", ")}
                </div>
              )}
              {form.medications.length > 0 && (
                <div style={{ fontSize: 12, color: "#a0aec0" }}>
                  Medications: {form.medications.map(m => `${m.name} ${m.dosage}`).join(", ")}
                </div>
              )}
            </div>

            {/* Assignment */}
            <div style={S.card}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Program & Assignment</div>
              <div style={{ fontSize: 12, color: "#a0aec0" }}>
                {PROGRAMS.find(p => p.value === form.program)?.label || form.program}
              </div>
              {form.assignedAgentName && <div style={{ fontSize: 12, color: "#a0aec0" }}>Agent: {form.assignedAgentName}</div>}
              {form.assignedClinicianName && <div style={{ fontSize: 12, color: "#a0aec0" }}>Clinician: {form.assignedClinicianName}</div>}
              {form.assignedPrescriberName && <div style={{ fontSize: 12, color: "#a0aec0" }}>Prescriber: {form.assignedPrescriberName}</div>}
            </div>

            {/* Consents */}
            <div style={S.card}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Consents</div>
              {form.consents.map(c => (
                <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#a0aec0", marginBottom: 4 }}>
                  {c.signed
                    ? <Check size={12} style={{ color: "#68d391" }} />
                    : <X size={12} style={{ color: c.required ? "#fc8181" : "#4a5568" }} />
                  }
                  <span style={{ color: c.signed ? "#68d391" : c.required ? "#fc8181" : "#4a5568" }}>{c.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Navigation Footer */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        padding: "12px 20px 24px", background: "linear-gradient(0deg, #0a0f1a 60%, transparent)",
        zIndex: 40,
      }}>
        <div style={{ display: "flex", gap: 10, maxWidth: 512, margin: "0 auto" }}>
          {!isFirstStep && (
            <button onClick={handleBack} style={{
              flex: 1, padding: "14px 0", borderRadius: 14, fontSize: 14, fontWeight: 700,
              background: "rgba(99,179,237,0.08)", color: "#63b3ed",
              border: "1px solid rgba(99,179,237,0.15)", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <ArrowLeft size={16} /> Back
            </button>
          )}

          {isLastStep ? (
            <button onClick={handleSubmit} disabled={saving} style={{
              flex: 2, padding: "14px 0", borderRadius: 14, fontSize: 14, fontWeight: 800,
              background: saving ? "rgba(104,211,145,0.3)" : "linear-gradient(135deg, #48bb78, #38a169)",
              color: "white", border: "none", cursor: saving ? "default" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              {saving ? "Saving..." : <><Check size={16} /> Complete Intake</>}
            </button>
          ) : (
            <button onClick={handleNext} style={{
              flex: 2, padding: "14px 0", borderRadius: 14, fontSize: 14, fontWeight: 800,
              background: "linear-gradient(135deg, #f6ad55, #ed8936)",
              color: "#1a202c", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              Next <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}